import React from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import List from "@mui/material/List";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { RowButton } from "../../components/mui";

/**
 * 畫面索引（設計稿 #screen-map，el-pm-template_4.html:2568-2612）
 *
 * 這一頁是改版期間的「地圖」：舊系統的人打開新版會先問「我以前那頁去哪了」，
 * 一列一列寫出「新畫面取代了哪些舊畫面」比口頭交接或另開一份文件可靠 ——
 * 文件會過期，這頁跟著路由一起被點到才會被發現寫錯。
 *
 * 整頁是靜態清單，刻意不接任何 API：這裡描述的是「改版怎麼收攏畫面」這件事，
 * 不是後端的資料，接 API 只會讓一頁說明文字也依賴登入態與網路。
 */

/**
 * 清單寫成常數而不是 27 段 JSX，是為了讓「畫面名 / 取代了什麼 / 導去哪」
 * 三件事排在同一行看得見：漏掉一條或導錯路由時，用眼睛掃就找得到。
 *
 * title 與 from 逐字取自設計稿 L2578-2605，不要改寫成自己的說法 ——
 * 這些句子是改版時跟使用者溝通過的對照說明。
 *
 * to 走的是這個專案真正的路由（不是設計稿的 data-goto）。設計稿是單檔範本，
 * 靠 data-goto 切 section；這裡照搬 data-goto 只會得到 27 個點不動的按鈕。
 *
 * note 只出現在「需要先選一筆才進得去」的畫面：它們的網址帶 id
 * （/Project/Detail/:id、/Task/:id、/Approvals/:taskId、/Project/Import/:id），
 * 索引頁手上沒有 id，硬湊一個進去就是假資料，所以先導到上層清單，
 * 並且把「還要再選一筆」寫在列上 —— 不寫的話使用者會以為自己點錯了。
 */
const SCREENS = [
  { title: "專案列表", from: "取代：專案列表（列表檢視＋卡片檢視兩頁合一）", to: "/Project/List" },
  { title: "新增專案", from: "取代：新增專案（分步驟填寫）", to: "/Project/Add" },
  {
    title: "專案詳情",
    from: "取代：專案詳情 · 工項管理＋專案資訊彈窗",
    to: "/Project/List",
    note: "請先於清單中選擇專案",
  },
  {
    title: "專案資料",
    from: "取代：里程碑、文件、成員、會議紀錄四個分頁",
    to: "/Project/List",
    note: "請先於清單中選擇專案",
  },
  { title: "新增工項", from: "取代：新增工項表單（分步驟填寫）", to: "/Task/New" },
  {
    title: "工項詳情",
    from: "取代：工項詳情 14 區塊＋底部代回報工具列",
    to: "/MyTask",
    note: "請先於個人工項或專案的工項分頁中選擇工項",
  },
  { title: "審核清單", from: "取代：簽核中心兩個分頁", to: "/Approvals" },
  {
    title: "審核畫面",
    from: "取代：審核唯讀視窗（21 區塊 → 需求 vs 做法並排）",
    to: "/Approvals",
    note: "請先於清單中選擇待審核的工項",
  },
  { title: "看板", from: "取代：看板（5 欄＋直排中文 → 4 欄）", to: "/Board" },
  {
    title: "時程（里程碑時程軸）",
    from: "取代：時程日曆 4 種檢視（專案／工項／RD／議程）",
    to: "/Schedule",
  },
  {
    title: "新增里程碑（新增）",
    from: "PM 與高階主管訂定交付客戶的項目與日期",
    to: "/Schedule/Goal/New",
  },
  {
    title: "工項排程（新增）",
    from: "RD 主管將工項歸入里程碑，系統計算時程是否足夠",
    to: "/Schedule/Goal/Plan",
  },
  { title: "本週到期項目", from: "時程的第二個檢視，取代舊版月曆", to: "/Schedule/Week" },
  { title: "檢測", from: "取代：專案原碼檢測＋專案弱點掃描兩頁", to: "/Scan" },
  {
    title: "設定",
    from: "取代：偏好設定＋修改密碼彈窗＋字級與深淺色切換",
    to: "/Setting/Preferences",
  },
  { title: "搜尋", from: "取代：全域搜尋彈窗（⌘K → Ctrl + K，並標示快捷鍵）", to: "/Search" },
  { title: "通知", from: "取代：通知中心抽屜（單字圖示 → 完整句子）", to: "/Notifications" },
  { title: "登入", from: "可勾選「記住我」；登入頁即可調整字級", to: "/Auth/Login" },
  { title: "忘記密碼（新增）", from: "系統未開放寄信，改由「請管理員重設」處理", to: "/Auth/ForgotPassword" },
  { title: "我的帳號（新增）", from: "右上角姓名選單：我的資料、變更密碼、新手指南、登出", to: "/Me" },
  { title: "變更密碼", from: "取代：左下角 16px 的鎖頭圖示彈窗", to: "/Me/Password" },
  {
    title: "帳號與角色管理（新增）",
    from: "建立帳號、變更角色、停用、重設密碼、離職交接",
    to: "/Setting/Admin",
  },
  { title: "角色權限表（新增）", from: "以淺顯文字說明六種角色的權限", to: "/Setting/Roles" },
  { title: "新手指南", from: "取代：僅介紹當前頁面的 4 步導覽彈窗", to: "/Guide" },
  {
    title: "匯入工項",
    from: "取代：工具列上與「新增工項」同大的匯入鈕",
    to: "/Project/List",
    note: "請先於清單中選擇專案",
  },
];

/**
 * 拿掉或併掉的舊畫面（設計稿 L2601-2610）。
 *
 * 只寫「不見了」會被當成功能被砍；每一條都要講清楚搬去哪，
 * 這樣找不到舊功能的人自己讀得懂，不必回頭問 PM。
 */
const REMOVED = [
  { name: "專案列表卡片檢視", why: "與列表檢視重複，僅保留一種。" },
  {
    name: "時程日曆的專案／工項／RD／議程四種檢視",
    why: "合併為「本週到期項目」一頁，並移除外露的內部 ID。",
  },
  { name: "全體聊天室", why: "使用率低，併入工項留言。" },
  {
    name: "使用導覽 4 步彈窗",
    why: "改為「新手指南」一頁，可隨時從右上角姓名選單進入。",
  },
  { name: "專案資訊彈窗", why: "內容移至專案詳情的「專案概況」摘要。" },
  {
    name: "修改密碼彈窗",
    why: "改為獨立的「變更密碼」畫面，可從右上角姓名選單或設定進入，不再使用左下角的鎖頭圖示。",
  },
];

function MapIndex() {
  const navigate = useNavigate();

  return (
    <Box component="section">
      <Typography variant="h1" component="h1">
        畫面索引
      </Typography>

      <Typography sx={{ mt: "var(--space-3)" }}>
        舊系統 27 個可抵達的畫面，整併為 29 個畫面、6 個入口（新增的是里程碑、帳號角色管理、登入登出等原本缺少或不易找到的功能）。點選下列任一列即可開啟該畫面。
      </Typography>

      {/* 卡片不加內距，讓清單列自己的內距貼齊卡片邊，列與列之間才會是一條
          連續的分隔線，而不是每一列各縮排一段 */}
      <Card sx={{ mt: "var(--space-6)" }}>
        <List disablePadding>
          {SCREENS.map((s) => (
            /*
              整列是一顆按鈕：規則「任何可點物件 ≥ 44px 且一定有文字」。
              點擊區域包含整行文字，不需要（也不該）另外放一顆只有箭頭的小按鈕。
            */
            <RowButton
              key={s.title}
              type="button"
              onClick={() => navigate(s.to)}
              title={s.title}
              meta={
                <>
                  <span>{s.from}</span>
                  {/* 導去上層清單的列才有這一句；沒有 note 的列不渲染空 span，
                      免得 meta 的 gap 撐出一段看不出用途的空白 */}
                  {s.note && <span>{s.note}</span>}
                </>
              }
            />
          ))}
        </List>
      </Card>

      <Typography variant="h3" component="h2" sx={{ mt: "var(--space-8)" }}>
        已移除或併入的舊畫面
      </Typography>
      <Stack spacing={1} sx={{ mt: "var(--space-3)" }}>
        {REMOVED.map((r) => (
          <Typography key={r.name}>
            <strong>{r.name}</strong>　{r.why}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
}

export default MapIndex;
